import { isDate, parseDate } from "./skyward.base.time";
import { RelativeDate } from "./skyward.ui.relativedatepicker";

const ignoredPropertiesForJson = [
    "IsRestricted", "Parameters", "FieldPath", "LeftFieldPath",
    "RightFieldPath", "FieldType", "LeftFieldType", "RightFieldType",
    "FieldIsOperation", "LeftFieldIsOperation", "RightFieldIsOperation",
    "FieldPathDisplay", "LeftFieldPathDisplay", "RightFieldPathDisplay"
];

const nonAttributeProperties = ["List", "SearchCondition"];

interface ServerSearchCondition
{
    // ToDo: Eventually we should populate this
    [key: string]: any;
}

interface SearchConditionGetResult
{
    SearchCondition?: SearchConditionGetResult | SearchConditionGetResult[];
    [key: string]: any;
}


export interface FieldData
{
    fieldName: string | undefined;
    fieldPath: string | undefined;
    dataObjectType: string | undefined;
    displayName: string | undefined;
    displayPath: string | undefined;
    fieldType: string | undefined;
    calculatedFieldName: string | undefined;
    isOperation: boolean | undefined;
    parameters: any;
}

interface BaseSearchCondition
{
    Type: Types | "";
    DataObjectType?: string;
    Version: number;

    // Only exist on the Web and not on the Server
    // Properties here should also be listed in ignoredPropertiesForJson
    IsRestricted: boolean;
    Parameters?: any;

    // Required for index signature
    [key: string]: any;
}

interface TypedStandardFilterSearchCondition extends BaseSearchCondition
{
    UniqueID?: string;
    DisplayName?: string;
    RelationshipPath?: string;
    SkywardID?: string;
    StandardFilterIndexes?: number[];
}

interface ExistsSearchCondition extends BaseSearchCondition
{
    ConditionType?: ConditionType;

    // - Version = 1 this is the traditional path
    // - Version > 1 this is the guid path
    RelationshipName?: string;

    // - Version = 1 this is null
    // - Version > 1 this is the traditional path
    RelationshipNameDisplay?: string;

    // Relationship Path display value for the UI
    RelationshipNameUIDisplay?: string;
}

interface SchemaObjectFilterSearchCondition extends BaseSearchCondition
{
    InterfaceName?: string;
    DisplayName?: string;
    RelationshipPath?: string;
}

interface TypedSearchCondition extends BaseSearchCondition
{
    ConditionType?: ConditionType;

    // Left Field Name
    //      - Version = 1 this is the traditional path
    //      - Version > 1 this is the guid path
    FieldName?: string;
    LeftFieldName?: string;

    // Left Field Name Display
    //      - Version = 1 this is null
    //      - Version > 1 this is the traditional path
    FieldNameDisplay?: string;
    LeftFieldNameDisplay?: string;

    // Left Field Display Name for the UI
    FieldNameUIDisplay?: string;

    Value?: any;
    Low?: any;
    High?: any;
    List?: { Value: any[] | null };

    ValueBinding?: string;
    LowBinding?: string;
    HighBinding?: string;
    ListBinding?: string;

    // Only exist on the Web and not on the Server
    // Properties here should also be listed in ignoredPropertiesForJson
    // Left Field Path
    FieldPath?: string;
    LeftFieldPath?: string;
    // Left Field Data Type
    FieldType?: string;
    LeftFieldType?: string;
    // Left Field Is Operation
    FieldIsOperation?: boolean;
    LeftFieldIsOperation?: boolean;
    // Left Field Path Display
    FieldPathDisplay?: string;
    LeftFieldPathDisplay?: string;
}

type FieldToFieldSearchCondition = Omit<TypedSearchCondition, "Value" | "Low" | "High" | "List" | "ValueBinding" | "LowBinding" | "HighBinding" | "ListBinding"> &
{
    // - Version = 1 this is the traditional path
    // - Version > 1 this is the guid path
    RightFieldName?: string;

    // - Version = 1 this is null
    // - Version > 1 this is the traditional path
    RightFieldNameDisplay?: string;

    // Right Field Display Name for the UI
    RightFieldNameUIDisplay?: string;

    CalculatedFieldName?: string;
    RightCalculatedFieldName?: string;

    RightFieldPath?: string;
    RightFieldType?: string;
    RightFieldIsOperation?: boolean;
    RightFieldPathDisplay?: string;
}

// Not supported in the filter builder but can be displayed in the filter viewer
type SubquerySearchCondition = Omit<TypedSearchCondition, "Value" | "Low" | "High" | "List" | "ValueBinding" | "LowBinding" | "HighBinding" | "ListBinding"> &
{
    OperandDisplayValue?: string;
}

type WebSearchCondition = TypedStandardFilterSearchCondition | ExistsSearchCondition | FieldToFieldSearchCondition | TypedSearchCondition;

interface WebSearchConditionGroup
{
    Type: Types;
    ConditionGroupType: ConditionGroupType;

    [key: string]: any;
}

export type SearchConditionGroupOrSingleSearchCondition = SearchCondition | SearchConditionGroup;

export type ConvertBindingToDisplayName = (binding: string) => string;

export interface FriendlyQueryValueToken
{
    Type: Omit<TokenType, TokenType.NestedTokens>,
    Value: string
}

export interface FriendlyQueryNestedToken
{
    Type: TokenType.NestedTokens,
    Value: FriendlyQueryToken[]
}

export type FriendlyQueryToken = FriendlyQueryValueToken | FriendlyQueryNestedToken;

export enum TokenType
{
    SearchCondition = "SearchCondition",
    GroupType = "GroupType",
    Exist = "Exist",
    NestedTokens = "NestedTokens",
}

export enum ConditionGroupType
{
    And = "And",
    Or = "Or"
}

export enum ConditionType
{
    Exists = "Exists",
    NotExists = "NotExists",
    True = "True" ,
    False = "False",
    Null = "Null" ,
    NotNull = "NotNull",
    BetweenInclusive = "BetweenInclusive",
    BetweenExclusive = "BetweenExclusive",
    NotBetweenInclusive = "NotBetweenInclusive",
    NotBetweenExclusive = "NotBetweenExclusive",
    Equal = "Equal",
    NotEqual = "NotEqual",
    List = "List",
    NotList = "NotList",
}

export enum Types
{
    FieldToFieldSearchCondition = "FieldToFieldSearchCondition",
    SchemaObjectFilterSearchCondition = "SchemaObjectFilterSearchCondition",
    DateTimeSearchCondition = "DateTimeSearchCondition",
    TimeSpanSearchCondition = "TimeSpanSearchCondition",
    TypedStandardFilterSearchCondition = "TypedStandardFilterSearchCondition",
    SearchConditionGroup = "SearchConditionGroup",
    AndSearchConditionGroup = "AndSearchConditionGroup",
    OrSearchConditionGroup = "OrSearchConditionGroup",
    LongSearchCondition = "LongSearchCondition",
    IntSearchCondition = "IntSearchCondition",
    ShortSearchCondition = "ShortSearchCondition",
    ByteSearchCondition = "ByteSearchCondition",
    BoolSearchCondition = "BoolSearchCondition",
    StringSearchCondition = "StringSearchCondition",
    DecimalSearchCondition = "DecimalSearchCondition",
    EnumSearchCondition = "EnumSearchCondition",
    ExistsSearchCondition = "ExistsSearchCondition",
    SubquerySearchCondition = "SubquerySearchCondition", // Not supported in the filter builder but can be displayed in the filter viewer
};

export enum FieldTypes
{
    NumberLong = "number long",
    NumberInt = "number int",
    NumberShort = "number short",
    NumberByte = "number byte",
    NumberDecimal = "number decimal",
    Boolean = "boolean",
    String = "string",
    Time = "time",
    DateTime = "datetime",
    TimeSpan = "timespan"
};

export function getSearchConditionTypeByFieldType(fieldType: string): Types
{
    switch (fieldType.toLowerCase())
    {
        case FieldTypes.NumberLong:
            return Types.LongSearchCondition;
        case FieldTypes.NumberInt:
            return Types.IntSearchCondition;
        case FieldTypes.NumberShort:
            return Types.ShortSearchCondition;
        case FieldTypes.NumberByte:
            return Types.ByteSearchCondition;
        case FieldTypes.NumberDecimal:
            return Types.DecimalSearchCondition;
        case FieldTypes.Boolean:
            return Types.BoolSearchCondition;
        case FieldTypes.String:
            return Types.StringSearchCondition;
        case FieldTypes.Time:
        case FieldTypes.DateTime:
            return Types.DateTimeSearchCondition;
        case FieldTypes.TimeSpan:
            return Types.TimeSpanSearchCondition;
        case "onetomany":
        case "manytoone":
            return Types.ExistsSearchCondition;
        case "standardfilter":
            return Types.TypedStandardFilterSearchCondition;
        default:
            return Types.EnumSearchCondition;
    }
}

export function getFromJson(value: ServerSearchCondition | string | null | undefined, convertBindingToDisplayName: ConvertBindingToDisplayName | null = null): SearchConditionGroupOrSingleSearchCondition
{
    if (value !== null && value !== undefined && value !== "")
    {
        const objectValue: ServerSearchCondition = typeof value === "string" ? JSON.parse(value) : value;

        const searchConditionType: Types = objectValue["@Type"];
        const isSearchConditionGroup = searchConditionType === Types.SearchConditionGroup || searchConditionType === Types.AndSearchConditionGroup || searchConditionType === Types.OrSearchConditionGroup;
        const condition: SearchConditionGroupOrSingleSearchCondition = isSearchConditionGroup ? new SearchConditionGroup() : new SearchCondition();
        const values: Record<string, any> = {};

        condition.ConvertBindingToDisplayName = convertBindingToDisplayName;

        for (const prop in objectValue)
        {
            const key = (prop.indexOf("@") === 0) ? prop.substring(1) : prop;
            values[key] = objectValue[prop];
        }

        condition.set(values as any);
        return condition;
    }

    return new SearchCondition();
}

export class SearchCondition
{
    private _searchCondition: WebSearchCondition = { Type: "", IsRestricted: false, DataObjectType: "", Version: 1 };
    private _existsCondition: SearchConditionGroupOrSingleSearchCondition | null = null;
    public ConvertBindingToDisplayName: ConvertBindingToDisplayName | null = null;

    private _safeConvertBindingToDisplayName(binding: string): string
    {
        return this.ConvertBindingToDisplayName?.(binding) || binding;
    }

    private _convertCalculatedFieldIDToDisplayName(calculatedFieldID?: string | null): string | null
    {
        if (calculatedFieldID)
        {
            const binding = `{${calculatedFieldID}}`;
            return this._safeConvertBindingToDisplayName(binding);
        }
        return null;
    }

    setType(type: Types)
    {
        this._searchCondition.Type = type;
    }

    set IsRestricted(isRestricted: boolean)
    {
        this._searchCondition.IsRestricted = isRestricted;
    }

    toJSON(): string
    {
        return JSON.stringify(this.get());
    }

    removeWhen(predicate: (sc: SearchCondition | SearchConditionGroup) => boolean): SearchCondition | SearchConditionGroup | undefined
    {
        if (predicate(this))
        {
            return undefined;
        }

        if (this._existsCondition)
        {
            this._existsCondition = this._existsCondition.removeWhen(predicate) || null;
        }

        return this;
    }

    get(): SearchConditionGetResult
    {
        const result: SearchConditionGetResult = {};

        for (const key of Object.keys(this._searchCondition))
        {
            if (!ignoredPropertiesForJson.includes(key))
            {
                const value = this._searchCondition[key];

                if (value !== undefined)
                {
                    const resultKey = nonAttributeProperties.indexOf(key) >= 0 ? key : `@${key}`;
                    result[resultKey] = value;
                }
            }
        }

        if (this._existsCondition)
        {
            result.SearchCondition = this._existsCondition.get();
        }

        return result;
    }

    getFieldsFromCondition(traverseExists: boolean): FieldData[]
    {
        if (this._searchCondition.IsRestricted || this._searchCondition.Type === Types.TypedStandardFilterSearchCondition)
        {
            return [];
        }

        const fields: FieldData[] = [];

        const leftField = this._searchCondition.FieldName ?? this._searchCondition.LeftFieldName;
        if (leftField)
        {
            const leftDisplayField = this._searchCondition.FieldNameDisplay ?? this._searchCondition.LeftFieldNameDisplay ?? this._searchCondition.FieldName ?? this._searchCondition.LeftFieldName;
            const leftDisplayPath = this._searchCondition.FieldPathDisplay ?? this._searchCondition.LeftFieldPathDisplay;
            const leftFieldPath = this._searchCondition.FieldPath ?? this._searchCondition.LeftFieldPath;
            const leftFieldType = this._searchCondition.FieldType ?? this._searchCondition.LeftFieldType;
            const leftFieldIsOperation = this._searchCondition.FieldIsOperation ?? this._searchCondition.LeftFieldIsOperation;
            fields.push(
            {
                fieldName: leftField,
                fieldPath: leftFieldPath,
                dataObjectType: this._searchCondition.DataObjectType,
                displayName: leftDisplayField,
                displayPath: leftDisplayPath,
                fieldType: leftFieldType,
                calculatedFieldName: this._searchCondition.CalculatedFieldName,
                isOperation: leftFieldIsOperation,
                parameters: this._searchCondition.Parameters
            });
        }

        const rightField = this._searchCondition.RightFieldName;
        if (rightField)
        {
            const rightDisplayField = this._searchCondition.RightFieldNameDisplay ?? this._searchCondition.RightFieldName;
            const rightDisplayPath = this._searchCondition.RightFieldPathDisplay;
            const rightFieldPath = this._searchCondition.RightFieldPath;
            const rightFieldType = this._searchCondition.RightFieldType;
            const rightFieldIsOperation = this._searchCondition.RightFieldIsOperation;
            fields.push(
            {
                fieldName: rightField,
                fieldPath: rightFieldPath,
                dataObjectType: this._searchCondition.DataObjectType,
                displayName: rightDisplayField,
                displayPath: rightDisplayPath,
                fieldType: rightFieldType,
                calculatedFieldName: this._searchCondition.RightCalculatedFieldName,
                isOperation: rightFieldIsOperation,
                parameters: this._searchCondition.Parameters
            });
        }

        const condition = this._searchCondition.ConditionType;
        if (traverseExists && (condition === ConditionType.Exists || condition === ConditionType.NotExists) && this._existsCondition)
        {
            fields.push(...this._existsCondition.getFieldsFromCondition(traverseExists));
        }
        return fields;
    }

    toFriendlyQuery(): string | null
    {
        return appendTokens(this.toTokenizedFriendlyQuery());

        function appendTokens(tokens: FriendlyQueryToken[]): string
        {
            let friendlyQueryParts: string[] = [];

            tokens.forEach(t =>
            {
                switch (t.Type)
                {
                    case TokenType.SearchCondition:
                    case TokenType.GroupType:
                    case TokenType.Exist:
                        friendlyQueryParts.push(`${t.Value} `);
                        break;
                    case TokenType.NestedTokens:
                        friendlyQueryParts.push(`${appendTokens(t.Value as FriendlyQueryToken[])} `);
                        break;
                }
            });

            let friendlyQuery = friendlyQueryParts.join("").trim();

            if (friendlyQuery.length > 0)
            {
                return `( ${friendlyQuery} )`;
            }

            return "";
        }
    }

    toTokenizedFriendlyQuery(): FriendlyQueryToken[]
    {
        const tokens: FriendlyQueryToken[] = [];

        // Check to make sure its not the default value of _searchCondition
        if (this._searchCondition.Type && this._searchCondition.Type != "")
        {
            if (this._searchCondition.IsRestricted)
            {
                tokens.push({ Type: TokenType.SearchCondition, Value: "***" });
            }
            else
            {
                const isVersionOne = !(this._searchCondition.Version > 1);

                if (this._searchCondition.Type === Types.TypedStandardFilterSearchCondition)
                {
                    const typedStandardFilterSearchCondition = this._searchCondition as TypedStandardFilterSearchCondition;
                    tokens.push({ Type: TokenType.SearchCondition, Value: `'${typedStandardFilterSearchCondition.DisplayName || ""}'` });
                }
                else if (this._searchCondition.Type === Types.SchemaObjectFilterSearchCondition)
                {
                    const schemaObjectFilterSearchCondition = this._searchCondition as SchemaObjectFilterSearchCondition;
                    tokens.push({ Type: TokenType.SearchCondition, Value: `'${schemaObjectFilterSearchCondition.DisplayName || ""}' Equal 'True'` });
                }
                else if (this._searchCondition.Type === Types.ExistsSearchCondition)
                {
                    const existsSeachCondition = this._searchCondition as ExistsSearchCondition;

                    const conditionType = this._searchCondition.ConditionType ?? ConditionType.Exists;
                    const relationshipDisplayValueFallback = isVersionOne ? existsSeachCondition.RelationshipName : existsSeachCondition.RelationshipNameDisplay;
                    const relationshipDisplayValue = existsSeachCondition.RelationshipNameUIDisplay ?? relationshipDisplayValueFallback;
                    const prefix = conditionType === ConditionType.Exists ? "At Least One" : "No";
                    const conditionTypeStr = this._existsCondition == null ? "Exist" : "Exist Where";

                    tokens.push({ Type: TokenType.SearchCondition, Value: `${prefix} '${relationshipDisplayValue}'` });
                    tokens.push({ Type: TokenType.Exist, Value: conditionTypeStr });

                    if (this._existsCondition)
                    {
                        let innerCondition = this._existsCondition;
                        // For the sake of displaying the friendly query we need the exists inner query to always be a search condition group
                        // so wrap the condition if its not a group
                        if (innerCondition instanceof SearchCondition)
                        {
                            const wrappingGroup = new SearchConditionGroup();
                            wrappingGroup.ConvertBindingToDisplayName = innerCondition.ConvertBindingToDisplayName;
                            wrappingGroup.addCondition(innerCondition);
                            innerCondition = wrappingGroup;
                        }

                        // Since this should always be a SearchConditionGroup there should only ever be either 0 or 1 inner token
                        for (let innerToken of innerCondition.toTokenizedFriendlyQuery())
                        {
                            tokens.push(innerToken);
                        }
                    }
                }
                else
                {

                    const typedSearchCondition = this._searchCondition as TypedSearchCondition;
                    const leftCalculatedFieldBinding = this._convertCalculatedFieldIDToDisplayName(this._searchCondition.CalculatedFieldName);

                    let leftDisplayValue: string;
                    if (isVersionOne)
                    {
                        leftDisplayValue = typedSearchCondition.FieldNameUIDisplay ?? typedSearchCondition.FieldName ?? leftCalculatedFieldBinding ?? typedSearchCondition.LeftFieldName ?? "";
                    }
                    else
                    {
                        leftDisplayValue = typedSearchCondition.FieldNameUIDisplay ?? typedSearchCondition.FieldNameDisplay ?? leftCalculatedFieldBinding ?? typedSearchCondition.LeftFieldNameDisplay ?? "";
                    }

                    let friendlyQuery = `'${leftDisplayValue}' ${(this._searchCondition.ConditionType ?? "").toDisplayName()}`;

                    let lowDisplayValue: any;
                    let highDisplayValue: any;

                    if (this._searchCondition.Type === Types.FieldToFieldSearchCondition)
                    {
                        const fieldToFieldSearchCondition = this._searchCondition as FieldToFieldSearchCondition;
                        const rightCalculatedFieldBinding = this._convertCalculatedFieldIDToDisplayName(fieldToFieldSearchCondition.RightCalculatedFieldName);

                        lowDisplayValue = fieldToFieldSearchCondition.RightFieldNameUIDisplay ?? fieldToFieldSearchCondition.RightFieldNameDisplay ?? fieldToFieldSearchCondition.RightFieldName ?? rightCalculatedFieldBinding;
                    }
                    else if (this._searchCondition.Type === Types.SubquerySearchCondition)
                    {
                        const subquerySearchCondition = this._searchCondition as SubquerySearchCondition;
                        lowDisplayValue = subquerySearchCondition.OperandDisplayValue ?? "Inner Search Results";
                    }
                    else if (this._searchCondition.Type === Types.DateTimeSearchCondition)
                    {
                        const lowBinding = typedSearchCondition.LowBinding ?? typedSearchCondition.ValueBinding ?? typedSearchCondition.ListBinding;
                        const lowValue = typedSearchCondition.Low ?? typedSearchCondition.Value;
                        const lowList = typedSearchCondition.List;

                        if (lowBinding)
                        {
                            lowDisplayValue = lowBinding;
                        }
                        else if (lowValue)
                        {
                            const lowDateValue = parseDateTimeValue(lowValue);
                            lowDisplayValue = lowDateValue !== null ? lowDateValue : lowValue;
                        }
                        else if (lowList && lowList.Value)
                        {
                            const displayValues: string[] = [];
                            for (let listItem of lowList.Value)
                            {
                                const listDateValue = parseDateTimeValue(listItem);
                                displayValues.push(listDateValue !== null ? listDateValue : listItem);
                            }
                            lowDisplayValue = displayValues.join(";");
                        }

                        const highValue = typedSearchCondition.High;
                        const highBinding = typedSearchCondition.HighBinding;

                        if (highBinding)
                        {
                            highDisplayValue = highBinding;
                        }
                        else if (highValue)
                        {
                            const highDateValue = parseDateTimeValue(highValue);
                            highDisplayValue = highDateValue !== null ? highDateValue : highValue;
                        }
                    }
                    else
                    {
                        lowDisplayValue = typedSearchCondition.LowBinding ?? typedSearchCondition.ValueBinding ?? typedSearchCondition.Low ?? typedSearchCondition.Value ??
                            typedSearchCondition.ListBinding ?? typedSearchCondition?.List?.Value?.join(";") ?? typedSearchCondition.List;

                        highDisplayValue = typedSearchCondition.HighBinding ?? typedSearchCondition.High;
                    }

                    if (lowDisplayValue !== null && lowDisplayValue !== undefined)
                    {
                        const updatedLowValue = this._safeConvertBindingToDisplayName(lowDisplayValue);
                        if (highDisplayValue !== null && highDisplayValue !== undefined)
                        {
                            const updatedHighValue = this._safeConvertBindingToDisplayName(highDisplayValue);
                            friendlyQuery += ` '${updatedLowValue}' And '${updatedHighValue}'`;
                        }
                        else
                        {
                            friendlyQuery += ` '${updatedLowValue}'`;
                        }
                    }

                    tokens.push({ Type: TokenType.SearchCondition, Value: friendlyQuery });
                }
            }
        }

        return tokens;

        function parseDateTimeValue(value: string | null): string | null
        {
            if (value !== null && value !== undefined && value !== "")
            {
                const parsedValue = parseDate(value);
                if (isDate(parsedValue, false))
                {
                    // FilterBuilder only supports Dates for DateTimeSearchConditions if that changes this will need to be updated
                    return parsedValue.toLocaleDateString();
                }
                else
                {
                    const relativeDate = RelativeDate.parse(value);
                    if (relativeDate != null)
                    {
                        return relativeDate.toString();
                    }

                    //falling back to displaying raw value
                    return value;
                }
            }

            return null;
        }
    }

    getValidationMessage(): string | null
    {
        const allowedNoValueConditionTypes = [ConditionType.True, ConditionType.False, ConditionType.Null, ConditionType.NotNull, ConditionType.List, ConditionType.NotList];
        const leftCalculatedFieldBinding = this._convertCalculatedFieldIDToDisplayName(this._searchCondition.CalculatedFieldName);

        let fieldName: string | null | undefined;
        if (this._searchCondition.Type === Types.FieldToFieldSearchCondition)
        {
            fieldName = this._searchCondition.LeftFieldName ?? leftCalculatedFieldBinding;
        }
        else if (this._searchCondition.Version > 1)
        {
            fieldName = this._searchCondition.FieldNameDisplay ?? leftCalculatedFieldBinding;
        }
        else
        {
            fieldName = this._searchCondition.FieldName ?? leftCalculatedFieldBinding;
        }

        if (fieldName !== null && fieldName !== undefined)
        {
            fieldName = this._safeConvertBindingToDisplayName(fieldName);
        }

        switch (this._searchCondition.Type)
        {
            case Types.TypedStandardFilterSearchCondition:
            case Types.SchemaObjectFilterSearchCondition:
                return null;
            default:
                if (!this._searchCondition.ConditionType)
                {
                    return fieldName ? `Condition Type must be set for ${fieldName}.` : "Condition Type must be set.";
                }

                if (this._searchCondition.Type !== Types.StringSearchCondition && this._searchCondition.Type !== Types.ExistsSearchCondition && allowedNoValueConditionTypes.indexOf(this._searchCondition.ConditionType) < 0)
                {
                    const hasValue = Boolean(this._searchCondition.Value || this._searchCondition.Value === false || this._searchCondition.ValueBinding);
                    const hasLow = Boolean(this._searchCondition.Low || this._searchCondition.LowBinding);
                    const hasHigh = Boolean(this._searchCondition.High || this._searchCondition.HighBinding);
                    const hasRightField = Boolean(this._searchCondition.RightFieldName || this._searchCondition.RightCalculatedFieldName);

                    if (!(hasValue || hasRightField || (hasLow && hasHigh)))
                    {
                        return fieldName ? `Condition must have a value for ${fieldName}.` : "Condition must have a value.";
                    }

                    const maxValue = this.getMaxNumericValue();
                    if (maxValue)
                    {
                        const message = ` is an invalid value. Condition value must be between -${maxValue} and ${maxValue}.`;
                        if (this._searchCondition.Value && Math.abs(this._searchCondition.Value) > maxValue)
                        {
                            return this._searchCondition.Value + message;
                        }
                        if (this._searchCondition.Low && Math.abs(this._searchCondition.Low) > maxValue)
                        {
                            return this._searchCondition.Low + message;
                        }
                        if (this._searchCondition.High && Math.abs(this._searchCondition.High) > maxValue)
                        {
                            return this._searchCondition.High + message;
                        }
                    }
                }
                return null;
        }
    }

    getMaxNumericValue(): number | null
    {
        /* Unable to use a value of 9223372036854775807 when the type is LongSearchCondition, since we don't support using BigInt yet due to old versions of Safari on older iOS devices.
         * To get around this limitation we are masking the search condition value to at most 18 characters instead of 19, which guaranties the input is a valid long and we can skip this validation.
         * See PR 5271305
         */
        switch (this._searchCondition.Type)
        {
            case Types.IntSearchCondition:
                return 2147483647;
            case Types.ShortSearchCondition:
                return 32767;
            case Types.ByteSearchCondition:
                return 255;
        }
        return null;
    }

    set(keyValues: Record<string, any>): void
    {
        for (const key in keyValues)
        {
            if (key === "SearchCondition" || key === "Condition")
            {
                this._existsCondition = getFromJson(keyValues[key], this.ConvertBindingToDisplayName);
            }
            else if (key === "List")
            {
                if (keyValues[key]?.Value && !Array.isArray(keyValues[key].Value))
                {
                    this._searchCondition.List = { Value: [keyValues.List.Value] };
                }
                else
                {
                    this._searchCondition.List = keyValues.List;
                }
            }
            else if (keyValues[key] != null)
            {
                this._searchCondition[key] = keyValues[key];
            }
            else if (this._searchCondition[key] !== undefined)
            {
                delete this._searchCondition[key];
            }
        }
    }
}

export class SearchConditionGroup
{
    private _searchConditionGroup: WebSearchConditionGroup = { Type: Types.SearchConditionGroup, ConditionGroupType: ConditionGroupType.And };
    private _conditions: SearchConditionGroupOrSingleSearchCondition[] = [];
    public ConvertBindingToDisplayName: ConvertBindingToDisplayName | null = null;

    removeEmptyConditions(): SearchConditionGroupOrSingleSearchCondition | null
    {
        const newConditions: SearchConditionGroupOrSingleSearchCondition[] = [];

        this._conditions.forEach(c =>
        {
            if (c instanceof SearchConditionGroup)
            {
                const cleanedCondition = c.removeEmptyConditions();
                if (cleanedCondition != null)
                {
                    newConditions.push(cleanedCondition);
                }
            }
            else
            {
                newConditions.push(c);
            }
        });


        if (newConditions.length === 0)
        {
            return null;
        }

        this._conditions = newConditions;

        return this;
    }

    removeWhen(predicate: (sc: SearchCondition | SearchConditionGroup) => boolean): SearchConditionGroupOrSingleSearchCondition | undefined
    {
        if (predicate(this))
        {
            return undefined;
        }

        for (const key in this._conditions)
        {
            const result = this._conditions[key].removeWhen(predicate);
            if (result !== undefined)
            {
                this._conditions[key] = result;
            }
            else
            {
                delete this._conditions[key];
            }
        }

        return this;
    }

    getFieldsFromCondition(traverseExists: boolean): FieldData[]
    {
        if (this._conditions.length === 0)
        {
            return [];
        }

        const conditionFields: FieldData[] = [];

        this._conditions.forEach(c =>
        {
            const fieldData = c.getFieldsFromCondition(traverseExists);
            if (fieldData?.length)
            {
                conditionFields.push(...fieldData);
            }
        });

        return conditionFields;
    }

    toTokenizedFriendlyQuery(): FriendlyQueryToken[]
    {
        const innerTokens: FriendlyQueryToken[] = [];

        if (this._conditions.length > 0)
        {
            let isFirst = true;
            this._conditions.forEach(innerCondition =>
            {
                if (innerCondition instanceof SearchCondition && isFirst)
                {
                    // Return a token with an empty string to fill the spot for the first line
                    // Need this for the vue FilterViewer to render correctly
                    innerTokens.push({ Type: TokenType.GroupType, Value: "" });
                }
                else if (!isFirst)
                {
                    innerTokens.push({ Type: TokenType.GroupType, Value: this._searchConditionGroup.ConditionGroupType });
                }

                for (const token of innerCondition.toTokenizedFriendlyQuery())
                {
                    innerTokens.push(token);
                }

                isFirst = false;
            });
        }

        if (innerTokens.length > 0)
        {
            return [{ Type: TokenType.NestedTokens, Value: innerTokens }]
        }
        else
        {
            return [];
        }
    }

    toFriendlyQuery(): string
    {
        if (this._conditions.length === 0)
        {
            return "";
        }

        if (this._conditions.length === 1)
        {
            return this._conditions[0].toFriendlyQuery() || "";
        }

        const conditionQueries: string[] = [];

        this._conditions.forEach(c =>
        {
            const fq = c.toFriendlyQuery();
            if (fq)
            {
                conditionQueries.push(fq);
            }
        });

        const joinWith = ` ${this._searchConditionGroup.ConditionGroupType} `;
        return `( ${conditionQueries.join(joinWith)} )`;
    }

    toJSON(): string
    {
        if (this._conditions.length === 0)
        {
            return "";
        }

        if (this._conditions.length === 1)
        {
            return this._conditions[0].toJSON();
        }

        return JSON.stringify(this.get());
    }

    getValidationMessage(): string
    {
        const messages: string[] = [];
        this._conditions.forEach(c =>
        {
            const msg = c.getValidationMessage();
            if (msg)
            {
                messages.push(msg);
            }
        });

        return messages.join("\r\n");
    }

    get(): SearchConditionGetResult
    {
        const result: SearchConditionGetResult = {};

        for (const key of Object.keys(this._searchConditionGroup))
        {
            if (!ignoredPropertiesForJson.includes(key))
            {
                const value = this._searchConditionGroup[key];

                if (value !== undefined)
                {
                    const resultKey = nonAttributeProperties.indexOf(key) >= 0 ? key : `@${key}`;
                    result[resultKey] = value;
                }
            }
        }

        result.SearchCondition = [];
        for (const condition of this._conditions)
        {
            if (condition !== null)
            {
                result.SearchCondition.push(condition.get());
            }
        }

        return result;
    }

    set(keyValues: Record<string, any>): void
    {
        for (const key in keyValues)
        {
            if (key === "SearchCondition")
            {
                if (Array.isArray(keyValues[key]))
                {
                    keyValues[key].forEach((v: any) => this.addCondition(getFromJson(v, this.ConvertBindingToDisplayName)));
                }
                else
                {
                    this.addCondition(getFromJson(keyValues[key], this.ConvertBindingToDisplayName));
                }
            }
            else if (keyValues[key] != null)
            {
                this._searchConditionGroup[key] = keyValues[key];
            }
            else if (this._searchConditionGroup[key] !== undefined)
            {
                delete this._searchConditionGroup[key];
            }
        }
    }

    addCondition(searchCondition: SearchConditionGroupOrSingleSearchCondition): void
    {
        let newCondition: SearchConditionGroupOrSingleSearchCondition | null = searchCondition;
        if (searchCondition instanceof SearchConditionGroup)
        {
            newCondition = searchCondition.removeEmptyConditions();
        }

        if (newCondition != null)
        {
            newCondition.ConvertBindingToDisplayName = this.ConvertBindingToDisplayName;
            this._conditions.push(newCondition);
        }
    }
}